import { Inbox, Mail } from "lucide-react";

import { useEmails } from "@/hooks/useEmails";
import { PropPeek } from "@/components/ui/PropPeek";

interface InboxPeekProps {
  /** The agent's inbox address, if provisioned. */
  agentEmailAddress?: string;
  /** Provision the agent inbox when none exists yet. */
  onCreateEmailAddress: () => Promise<void> | void;
  /** Deep-link to the full /inbox page. */
  onOpenInbox: () => void;
}

/**
 * The mailbox "door": a glanceable peek at recent mail that opens the full
 * /inbox page. Mirrors IntegrationsPeek / NotebookQuickView — the world never
 * re-hosts the whole inbox, it points at it. Setup (provisioning the address,
 * allowlist) lives on the page, not here.
 */
export function InboxPeek({ agentEmailAddress, onCreateEmailAddress, onOpenInbox }: InboxPeekProps) {
  const { emails, unreadCount } = useEmails();
  const recent = emails.slice(0, 5);

  return (
    <PropPeek
      icon={Inbox}
      title="Inbox"
      meta={agentEmailAddress ? (unreadCount > 0 ? `${unreadCount} unread` : "All read") : undefined}
      openLabel="Open inbox"
      onOpenFull={onOpenInbox}
    >
      {!agentEmailAddress ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
          <Mail className="h-7 w-7 text-black/30 dark:text-white/30" strokeWidth={1.8} />
          <p className="text-[13px] leading-relaxed text-black/55 dark:text-white/55">
            Give your agent its own email address so it can send and receive mail on your behalf.
          </p>
          <button
            onClick={() => void onCreateEmailAddress()}
            className="rounded-full bg-home-yellow px-5 py-2 text-[13px] font-bold text-home-ink shadow-[0_8px_20px_-10px_rgba(0,0,0,0.4)] transition-transform hover:-translate-y-0.5"
          >
            Create inbox address
          </button>
        </div>
      ) : recent.length === 0 ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
          <Inbox className="h-7 w-7 text-black/25 dark:text-white/25" strokeWidth={1.8} />
          <p className="text-[13px] text-black/45 dark:text-white/45">No mail yet.</p>
          <p className="font-mono text-[11px] text-black/40 dark:text-white/40">{agentEmailAddress}</p>
        </div>
      ) : (
        <ul className="space-y-0.5 px-1">
          {recent.map((email) => {
            const sender = email.from.includes("<")
              ? email.from.split("<")[0].trim()
              : email.from.split("@")[0];
            return (
              <li key={email._id} className="flex items-center gap-2.5 rounded-lg px-2 py-2">
                {!email.isRead && (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-coral" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className={`truncate text-[13px] ${email.isRead ? "font-medium text-black/70 dark:text-white/70" : "font-bold text-black dark:text-white"}`}>
                      {sender}
                    </span>
                    <span className="ml-auto shrink-0 text-[11px] text-black/35 dark:text-white/35">
                      {formatRelativeTime(email.receivedAt)}
                    </span>
                  </div>
                  <div className={`truncate text-[12px] ${email.isRead ? "text-black/50 dark:text-white/50" : "text-black/70 dark:text-white/70"}`}>
                    {email.subject}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </PropPeek>
  );
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}
